/**
 * veraltet, nach Welle 2 entfernen.
 *
 * B-GTCC-UI-ADOPT1: the JSX-producing pieces of this module that duplicate a
 * `ui/primitives.tsx` component (`ControlDot`, `ControlNotMeasurable`,
 * `ControlRow`) are now thin wrappers around it — no call site in this repo
 * still imports them (every migrated view builds its own small local adapter
 * directly on `ui/primitives`, see `HostTable.tsx`), but the names stay as a
 * compatibility shim for any other in-flight lane that may still reference
 * them by name. They are kept only for that reason and should be deleted, not
 * merely re-pointed, once every lane has migrated (Welle 2).
 *
 * `ControlMono`, `ControlFreshBadge`, `ControlHead`, `ControlTiles`,
 * `ControlTile`, `ControlPanel`, `ControlActionRow`, `ControlNote`,
 * `ControlTableWrap` and the table/head/tile layout constants have no 1:1
 * counterpart in `ui/primitives.tsx` (its `Tile`/`Panel`/`TableWrap` follow
 * the Cockpit anatomy, not this module's Operations-Overview/Risk-Radar one)
 * and stay real, unchanged implementations.
 *
 * The tones and formatters (`ControlTone`, `toneOfStatus`, `toneOfVerdict`,
 * `toneColor`, `measuredNumber`, `formatInteger`, `formatDecimal1`,
 * `formatWholePercent`, `formatPercent2`) have no equivalent in
 * `ui/primitives.tsx` either — it deals in pre-formatted strings only, not in
 * DTOs or locales — so they stay real, actively imported domain code.
 *
 * Honesty rules (Owner P8/P9/P10): nothing here formats a value into
 * existence, and nothing reads a clock. A caller with no measurement passes
 * `null` and renders {@link ControlNotMeasurable}.
 */
import { Card, Stack } from "@genius-tracker/design-system";
import { activeLocale, type Locale } from "@genius-tracker/i18n";
import type { CSSProperties, ReactNode } from "react";

import type { GtccStatus, GtccVerdict } from "../../api/gtcc-result.js";
import { Dot, Row } from "../../ui/primitives.js";
import "./control-layout.css";
import { useGtccHeadTabs } from "./HeadTabsContext.js";

/* ----------------------------------------------------------------- tones */

export type ControlTone = "ok" | "warn" | "crit" | "info" | "muted";

const TONE_COLOR: Readonly<Record<ControlTone, string>> = {
  ok: "var(--gt-sys-success)",
  warn: "var(--gt-sys-warning)",
  crit: "var(--gt-sys-danger)",
  info: "var(--gt-sys-info)",
  muted: "var(--gt-sys-text-muted)",
};

export function toneColor(tone: ControlTone): string {
  return TONE_COLOR[tone];
}

/** `status` of the API convention → the dot/frame colour of the reference. */
export function toneOfStatus(status: GtccStatus): ControlTone {
  if (status === "healthy") return "ok";
  if (status === "warning" || status === "stale") return "warn";
  if (status === "critical") return "crit";
  return "muted";
}

/** `verdict` of a row → the same five tones. */
export function toneOfVerdict(verdict: GtccVerdict): ControlTone {
  if (verdict === "ok") return "ok";
  if (verdict === "warn") return "warn";
  if (verdict === "crit") return "crit";
  if (verdict === "info") return "info";
  return "muted";
}

/* ------------------------------------------------------------ formatters */

/**
 * The gate every payload number passes before it is formatted.
 *
 * The DTO types promise `number | null`, but a response that omits the field
 * arrives as `undefined` and `Intl.NumberFormat` would then print `NaN` — a
 * value the Owner rules forbid a panel to show (P9: unmeasurable is
 * "nicht messbar", never an invented or broken number). `null` here always
 * means "no measurement", so the caller renders `ControlNotMeasurable`.
 */
export function measuredNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * The same gate for a list. A 200 that carries no array where the DTO promises
 * one is a fault of the source; the panel then shows the measured empty state
 * instead of crashing (P10: never an invented number, but never a crash in
 * place of a statement either).
 *
 * `views/dplane/components/DplanePanels.tsx` carries the identical helper for
 * the Data-Plane tabs; folding the two into one module belongs to the shell
 * package D-GTCC-SHELL1, which is where the old views are removed.
 */
export function asList<T>(value: ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
  return Array.isArray(value) ? value : [];
}

/** Whole numbers — host counts, vehicles, alerts — in the locale's digits. */
export function formatInteger(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/** `96,8` — one decimal, the way the reference writes Data Confidence. */
export function formatDecimal1(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

/** `94 %` — whole percent, spaced by the locale. */
export function formatWholePercent(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(value / 100);
}

/** `99,97 %` — two decimals, the way the reference writes VPS uptime. */
export function formatPercent2(value: number, locale: Locale = activeLocale()): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/* ------------------------------------------------------ re-export shim */

/** Re-export of `ui/primitives.tsx`'s `Dot`, wrapped for `data-testid`. */
export function ControlDot(props: { tone: ControlTone; testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId}>
      <Dot tone={props.tone} />
    </span>
  );
}

/* ---------------------------------------------------------------- pieces */

const MONO_STYLE: CSSProperties = {
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  color: "var(--gt-sys-text)",
};

/** `.mono` — the right-hand value of a panel row, a table cell, a note. */
export function ControlMono(props: {
  children: ReactNode;
  tone?: ControlTone | undefined;
  testId?: string;
}): ReactNode {
  return (
    <span
      data-testid={props.testId}
      style={
        props.tone === undefined ? MONO_STYLE : { ...MONO_STYLE, color: toneColor(props.tone) }
      }
    >
      {props.children}
    </span>
  );
}

/**
 * `.fresh` — the age badge next to the `h1`. The text is built by the caller
 * from the payload's `freshnessSeconds` / `measuredAt`; this component never
 * derives an age, so a stale source can never look fresh.
 *
 * Anatomie der Referenz (Zeile 65-68): Pille mit Rahmen, davor ein runder
 * Punkt — gruen, solange die Quelle frisch ist, und in Warnfarbe samt
 * eingefaerbtem Rahmen, sobald sie es nicht mehr ist. Vorher stand hier
 * nackter Mono-Text ohne Pille und ohne Punkt (Abnahme E-GTCC-ACCEPT1).
 */
const FRESH_BADGE_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-1)",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  borderStyle: "solid",
  borderWidth: "0.0625rem",
  borderRadius: "1.25rem",
  paddingBlock: "0.125rem",
  paddingInline: "var(--gt-sys-space-2)",
};

export function ControlFreshBadge(props: {
  text: string;
  stale?: boolean;
  testId?: string;
}): ReactNode {
  const stale = props.stale === true;
  const color = stale ? toneColor("warn") : "var(--gt-sys-text-muted)";
  const style: CSSProperties = {
    ...FRESH_BADGE_STYLE,
    color,
    borderColor: stale ? toneColor("warn") : "var(--gt-sys-border)",
  };
  return (
    <span data-testid={props.testId} data-stale={stale ? "true" : "false"} style={style}>
      <ControlDot tone={stale ? "warn" : "ok"} />
      {props.text}
    </span>
  );
}

/** Grey "nicht messbar" — the mono/muted text pair `ui/primitives.tsx` styles via CSS classes. */
export function ControlNotMeasurable(props: { label: string; testId?: string }): ReactNode {
  return (
    <span data-testid={props.testId} data-unmeasurable="true" className="mono muted">
      {props.label}
    </span>
  );
}

/* ------------------------------------------------------------------ head */

const HEAD_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

const TITLE_STYLE: CSSProperties = {
  margin: 0,
  /* C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
     (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Der
     Token-Satz traegt jetzt alle drei Werte, damit jede Deck-View denselben
     Titel hat (vorher: 15/19/20/24 px je View, Gewicht 600). */
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--tx)",
};

const SUB_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-sm)",
  color: "var(--gt-sys-text-muted)",
};

/*
 * Head of the reference: title + `.fresh` + `.sub`. The reference is a
 * standalone page, so its title is an `h1`; inside the deck shell the `h1`
 * already belongs to the page frame, and every other view titles itself with
 * an `h2` (see `GtccCockpitView.tsx`). Visual weight stays the reference's.
 */
export function ControlHead(props: {
  title: string;
  subtitle: string;
  fresh?: ReactNode;
  testId?: string;
}): ReactNode {
  const headTabs = useGtccHeadTabs();
  return (
    <div data-testid={props.testId}>
      <div style={HEAD_STYLE}>
        <h2
          style={TITLE_STYLE}
          data-testid={props.testId === undefined ? undefined : `${props.testId}-title`}
        >
          {props.title}
        </h2>
        {props.fresh}
      </div>
      <p
        style={SUB_STYLE}
        data-testid={props.testId === undefined ? undefined : `${props.testId}-sub`}
      >
        {props.subtitle}
      </p>
      {headTabs}
    </div>
  );
}

/* ----------------------------------------------------------------- tiles */

const TILES_STYLE: CSSProperties = {
  display: "grid",
  gap: "var(--gt-sys-space-3)",
  gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))",
};

/** `.tiles` — the verdict-framed tile strip above the main grid. */
export function ControlTiles(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <div data-testid={props.testId} style={TILES_STYLE}>
      {props.children}
    </div>
  );
}

export const TILE_LABEL_STYLE: CSSProperties = {
  margin: 0,
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  letterSpacing: "0.06em",
  color: "var(--gt-sys-text-muted)",
};

export const TILE_BIG_STYLE: CSSProperties = {
  /* `.big` der Referenz ist 23 px; `font-size-xl` (16 px) hat die Kennzahl
     kleiner gesetzt als die Kachel traegt (Abnahme E-GTCC-ACCEPT1). */
  fontSize: "var(--gt-sys-font-size-2xl)",
  lineHeight: 1.1,
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  fontVariantNumeric: "tabular-nums",
  color: "var(--gt-sys-text)",
};

const TILE_SMALL_STYLE: CSSProperties = {
  marginInlineStart: "var(--gt-sys-space-1)",
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-regular)",
  color: "var(--gt-sys-text-muted)",
};

/**
 * `.card` der Referenz von aussen: Rahmen, Radius, Polsterung und die
 * eingefaerbte linke Kante, die den Ton traegt. Als geteilte Konstante, weil
 * `LaunchView` dieselbe Anatomie ohne die `.st`-Statuszeile braucht und der
 * Rohwert der Kantenbreite genau einmal stehen soll (Abnahme E-GTCC-ACCEPT1).
 * Die Tonfarbe setzt die Aufrufstelle als `borderInlineStartColor`.
 */
export const TILE_FRAME_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-1)",
  padding: "var(--gt-sys-space-3)",
  borderRadius: "var(--gt-sys-radius-md)",
  border: "1px solid var(--gt-sys-border)",
  borderInlineStartWidth: "0.1875rem",
  background: "var(--gt-sys-surface-raised)",
};

/**
 * `.card` — heading, big number with an optional `<small>` suffix, and the
 * `.st` status line. The frame follows the tile's own verdict; it is never set
 * from the sample classes of the reference, because `GTCC_REFERENCE_SPEC.md`
 * states that the reference's numbers are sample data and only its shape
 * ("column layout, cell composition, status semantics") is binding.
 */
export function ControlTile(props: {
  label: string;
  value: ReactNode;
  suffix?: string | undefined;
  statusText: string;
  tone: ControlTone;
  statusTone: ControlTone;
  testId?: string;
}): ReactNode {
  return (
    <div
      data-testid={props.testId}
      data-tone={props.tone}
      style={{ ...TILE_FRAME_STYLE, borderInlineStartColor: toneColor(props.tone) }}
    >
      <h3 style={TILE_LABEL_STYLE}>{props.label}</h3>
      <div style={TILE_BIG_STYLE}>
        {props.value}
        {props.suffix === undefined ? null : <small style={TILE_SMALL_STYLE}>{props.suffix}</small>}
      </div>
      <div
        data-testid={props.testId === undefined ? undefined : `${props.testId}-status`}
        data-tone={props.statusTone}
        style={{
          fontSize: "var(--gt-sys-font-size-xs)",
          color: toneColor(props.statusTone),
        }}
      >
        {props.statusText}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- panels */

/*
 * `.panel h2` der Referenz steht auf `justify-content: space-between`
 * (`gtcc/ui/cockpit-ui.css`): der Titel links, die `.mono muted` Ergaenzung am
 * rechten Rand der Tafel. Ohne diese eine Zeile reiht das Flexbox-Layout beide
 * dicht hintereinander — der Befund „Panel-Meta inline statt rechtsbuendig"
 * der Abnahme E-GTCC-ACCEPT2. Tafeln ohne Ergaenzung sehen unveraendert aus,
 * weil ein einzelnes Kind auch bei `space-between` am Anfang steht.
 */
export const PANEL_HEADING_STYLE: CSSProperties = {
  margin: 0,
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: "var(--gt-sys-space-2)",
  flexWrap: "wrap",
  fontSize: "var(--gt-sys-font-size-md)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  color: "var(--gt-sys-text)",
};

/**
 * `.panel` — heading plus rows, on the design system's flat card.
 *
 * `note` is the reference's muted mono suffix of the panel heading
 * (`<h2>Titel <span class="mono muted">Zusatz</span></h2>`). It is part of the
 * heading text, not a second line: `PANEL_HEADING_STYLE` is already the
 * baseline-aligned flex row the reference's `h2` is.
 */
export function ControlPanel(props: {
  title: string;
  note?: string;
  children: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <Card elevation={0} data-testid={props.testId}>
      <Stack gap={2}>
        {/* h3, nicht h2: die Deck-Konvention laesst je View genau eine
            Ueberschrift der Ebene 1/2 zu — das ist der Titel der Kopfzeile. */}
        <h3 style={PANEL_HEADING_STYLE}>
          {props.title}
          {props.note === undefined ? null : (
            <ControlMono
              tone="muted"
              {...(props.testId === undefined ? {} : { testId: `${props.testId}-note` })}
            >
              {props.note}
            </ControlMono>
          )}
        </h3>
        {props.children}
      </Stack>
    </Card>
  );
}

/**
 * `.grid-main` — the two-column body of the reference, left column wider.
 * Als Klasse statt als Style-Objekt: die Spaltenteilung 1.9fr zu 1fr und der
 * Umbruch unter 1100 px stehen in `control-layout.css`, weil ein Inline-Style
 * keine Medienabfrage kennt.
 */
export const GRID_MAIN_CLASS = "gtcc-grid-main";

/** A column of panels inside `.grid-main`. */
export const GRID_COLUMN_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--gt-sys-space-3)",
  minWidth: 0,
};

/* A-GTCC-ROWGRID1: 6 px und 12,5 px wie die `.row` der Referenz (Zeile 109),
   nicht 8 px und 13 px — die Abweichung war Teil der Ursache 2 des Abgleichs
   A-GTCC-ACCEPT-ALL2. */
export const ROW_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--gt-sys-space-2)",
  paddingBlock: "var(--gt-cmp-gtcc-row-pad)",
  fontSize: "var(--gt-cmp-gtcc-table-size)",
  color: "var(--gt-sys-text)",
};

/**
 * Der nachlaufende Wert der Button-Zeile.
 *
 * `ControlRow` gibt seit B-GTCC-VIEW-DEV1 an das kanonische `Row` aus
 * `ui/primitives.tsx` ab; `ControlActionRow` kann das nicht, weil `Row` ein
 * `div` zeichnet und diese Zeile ein `button` ist. Deshalb bleibt diese eine
 * Konstante hier — nicht als zweite Row-Implementierung, sondern als das
 * Stueck Layout, das der Button-Variante fehlt.
 */
const ROW_VALUE_STYLE: CSSProperties = { marginInlineStart: "auto" };

/** Re-export of `ui/primitives.tsx`'s `Row`. */
export function ControlRow(props: {
  tone: ControlTone;
  label: ReactNode;
  value?: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId} data-tone={props.tone}>
      <Row tone={props.tone} value={props.value}>
        {props.label}
      </Row>
    </div>
  );
}

/**
 * The same row as an activator — the reference's Quick Actions are rows that
 * navigate ("→ Monitoring Cockpit öffnen"). A `button` rather than a link
 * because the target is a view of this single-page shell.
 */
export function ControlActionRow(props: {
  tone: ControlTone;
  label: string;
  /**
   * The trailing `.mono` of the reference's row ("bearbeiten →", "Preview →
   * getippte Bestätigung → Countdown → Execute"). Optional, so the rows that
   * had none keep rendering exactly as before.
   */
  value?: ReactNode;
  onActivate: () => void;
  testId?: string;
}): ReactNode {
  return (
    <button
      type="button"
      data-testid={props.testId}
      data-tone={props.tone}
      onClick={props.onActivate}
      style={{
        ...ROW_STYLE,
        width: "100%",
        textAlign: "start",
        background: "none",
        border: "none",
        cursor: "pointer",
        font: "inherit",
        fontSize: "var(--gt-sys-font-size-sm)",
        color: "var(--gt-sys-text)",
      }}
    >
      <ControlDot tone={props.tone} />
      <span>{props.label}</span>
      {props.value === undefined ? null : <span style={ROW_VALUE_STYLE}>{props.value}</span>}
    </button>
  );
}

/**
 * Ein Bedienelement, das die Referenz als Text schreibt, nicht als Knopf.
 *
 * Die Referenz kennt im Inhaltsbereich keine gerahmten Schaltflaechen: eine
 * Aktion steht dort als `.mono`-Text in der Zeile, in der sie wirkt
 * (`[Run dry-run preview]`, `Filter 7/30/90 Tage` — Referenzzeilen 963/978).
 * Ein `Button` des Design-Systems traegt Rahmen, Polsterung und Mindesthoehe
 * und sprengt damit die Zeilenhoehe der Referenz; deshalb hier die
 * Text-Variante. Semantisch bleibt es ein `button` — die Aktion ist eine
 * Aktion, kein Link, und `aria-pressed` traegt den Zustand einer Auswahl.
 */
export function ControlTextAction(props: {
  label: string;
  onActivate: () => void;
  disabled?: boolean;
  /** Gesetzt, wenn das Element eine Auswahl ist: `true` = die gewaehlte. */
  pressed?: boolean;
  /**
   * Die `[…]` der Referenz. Sie sind Schmuck und stehen deshalb als eigene,
   * `aria-hidden`-Knoten neben dem Beschriftungstext — so bleibt der Name des
   * Knopfs der uebersetzte Katalogtext und keine zusammengesetzte Zeichenkette.
   */
  brackets?: boolean;
  /**
   * Ein zugaenglicher Name, der mehr sagt als das Etikett. Nur setzen, wenn
   * mehrere gleich beschriftete Aktionen auf einer Seite stehen; der sichtbare
   * Text muss darin vorkommen (WCAG 2.5.3, „Label in Name").
   */
  ariaLabel?: string;
  /** `aria-expanded`/`aria-controls`, wenn die Aktion einen Bereich aufklappt. */
  expanded?: boolean;
  controls?: string;
  testId?: string;
}): ReactNode {
  const disabled = props.disabled === true;
  const pressed = props.pressed === true;
  return (
    <button
      type="button"
      data-testid={props.testId}
      onClick={props.onActivate}
      disabled={disabled}
      aria-pressed={props.pressed === undefined ? undefined : pressed}
      aria-label={props.ariaLabel}
      aria-expanded={props.expanded}
      aria-controls={props.controls}
      style={{
        ...MONO_STYLE,
        color: pressed ? "var(--gt-sys-text)" : "var(--gt-sys-text-muted)",
        fontWeight: pressed
          ? "var(--gt-sys-font-weight-semibold)"
          : "var(--gt-sys-font-weight-regular)",
        background: "none",
        border: "none",
        padding: 0,
        textAlign: "start",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {props.brackets === true ? <span aria-hidden="true">[</span> : null}
      {props.label}
      {props.brackets === true ? <span aria-hidden="true">]</span> : null}
    </button>
  );
}

/** A muted mono sentence under the rows — the reference's "Radar-Logik". */
export function ControlNote(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <p
      data-testid={props.testId}
      style={{ ...MONO_STYLE, margin: 0, color: "var(--gt-sys-text-muted)" }}
    >
      {props.children}
    </p>
  );
}

/* ----------------------------------------------------------------- table */

/** `.tbl-wrap` — the horizontal scroll container of the reference's tables. */
export function ControlTableWrap(props: { children: ReactNode; testId?: string }): ReactNode {
  return (
    <div data-testid={props.testId} style={{ overflowX: "auto", minWidth: 0 }}>
      {props.children}
    </div>
  );
}

export const TABLE_STYLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  /* 12,5 px wie `table` der Referenz (Zeile 101), nicht der naechstliegende
     Skalenschritt `--gt-sys-font-size-sm` (13 px) — der Unterschied schlaegt
     ueber `line-height:1.55` voll auf das Zeilenraster durch. */
  fontSize: "var(--gt-cmp-gtcc-table-size)",
};

export const TH_STYLE: CSSProperties = {
  textAlign: "start",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
  fontSize: "var(--gt-cmp-gtcc-table-head-size)",
  fontWeight: "var(--gt-sys-font-weight-bold)",
  letterSpacing: "0.1em",
  color: "var(--gt-sys-text-muted)",
  whiteSpace: "nowrap",
};

/* `td` of the reference: 7px block padding, vertically centred, figures on a
 * fixed advance so columns of numbers line up. `verticalAlign: "top"` used to
 * push single-line rows apart. */
export const TD_STYLE: CSSProperties = {
  paddingBlock: "var(--gt-cmp-gtcc-table-cell-pad)",
  paddingInline: "var(--gt-sys-space-2)",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  color: "var(--gt-sys-text)",
};

/**
 * A-GTCC-VIEW-ALIN-AUDIT1 — die untere Trennlinie der Referenz.
 *
 * Die Referenz setzt sie auf jedem `th` und jedem `td`
 * (`GTCC_REFERENCE_2026-09-01.html:102-103`), nicht auf der Zeile. Fuenf
 * Sichten haben dieselben drei Rohwerte je einmal abgeschrieben
 * (`alin`, `cockpit/tabs/InfraTab`, `dplane`, `launch`, `ten`); hier steht
 * der Satz einmal. Nicht in `TH_STYLE`/`TD_STYLE` hineingezogen, weil das
 * die Linie in einem Zug auch den Sichten gaebe, die dieses Paket nicht
 * messen darf — sie wird an der Aufrufstelle aufgesetzt.
 */
export const CELL_RULE_STYLE: CSSProperties = {
  borderBlockEndWidth: 1,
  borderBlockEndStyle: "solid",
  borderBlockEndColor: "var(--gt-sys-border)",
};

/**
 * `.mono` der Referenz als Tabellenzelle (`GTCC_REFERENCE_2026-09-01.html:128`).
 *
 * Eigene Konstante und nicht `MONO_STYLE`: `MONO_STYLE` ist der Fliesstext
 * einer Panelzeile, diese hier ist eine Zelle und traegt deshalb Polsterung,
 * vertikale Ausrichtung und Ziffernbreite von `TD_STYLE`. Die Groesse kommt
 * aus `--gt-cmp-gtcc-mono-size` (11 px), nicht aus `--gt-sys-font-size-xs`
 * (12 px) — der Unterschied streckt die Zelle ueber `line-height:1.55` um
 * anderthalb Punkte.
 */
export const MONO_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-cmp-gtcc-mono-size)",
};

/* `.host` — the tenant/host cell. The reference only sets `font-weight:600`;
 * accent colour plus mono read as a link the row does not offer. */
export const HOST_CELL_STYLE: CSSProperties = {
  ...TD_STYLE,
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  whiteSpace: "nowrap",
};

/**
 * The one place every GTCC view masks a device identifier, so a reader never
 * sees a full IMEI in a table that only needs to distinguish rows (D-GTCC-
 * VIEW-TRIAGE1 gw-5). Kept here, not per view, so a second table can never
 * drift back to printing the value in full.
 */
export function maskImei(imei: string): string {
  if (typeof imei !== "string") return "—";
  return imei.length <= 8 ? imei : `${imei.slice(0, 4)}…${imei.slice(-4)}`;
}
